import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import axios from 'axios';
import SimpleDEXArtefact from './abis/SimpleDEX.json';
import IERC20Artefact from './abis/IERC20.json'; // ERC20-compatible artefact
import DaiTokenArtefact from './abis/DaiToken.json';
import UsdcTokenArtefact from './abis/UsdcToken.json';
import CarbonArtefact from './abis/Carbon.json';
import ErgonArtefact from './abis/Ergon.json'; // Ergon artefact
// Add more artefacts as needed
import './App.css';
import ChatWidget from './ChatWidget';
import PairChart from './PairChart';
import AdminDashboard from './AdminDashboard';
import { ThemeProvider, createTheme } from '@mui/material/styles';

// Extract ABIs from artefacts
const SimpleDEXAbi = SimpleDEXArtefact.abi;
const IERC20Abi = IERC20Artefact.abi;

// --- Constants ---
const IS_UI_ONLY = process.env.REACT_APP_IS_UI_ONLY === 'true'; // Set to true to work on UI without a backend/wallet.
const API_URL = process.env.REACT_APP_API_URL;
const WS_URL = process.env.REACT_APP_WS_URL;

console.log('[App.js] IS_UI_ONLY:', IS_UI_ONLY);
console.log('[App.js] API_URL:', API_URL);
console.log('[App.js] WS_URL:', WS_URL);

// This should be dynamically set based on the environment (e.g., from an env file)
const DEX_CONTRACT_ADDRESS = process.env.REACT_APP_DEX_CONTRACT_ADDRESS;

// Add pseudo-token for native ETH (or network native token)
const NATIVE_TOKEN = {
    name: 'ETH',
    address: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE', // Use correct checksum address to match contract
    decimals: 18
};

// --- Token ABI mapping ---
const TOKEN_ABI_MAP = {};
// Map canonical addresses (lowercase) to their ABIs using env vars

const REACT_NETWORK_ID = "1234";
if (DaiTokenArtefact["networks"][REACT_NETWORK_ID]["address"]) {    
    TOKEN_ABI_MAP[DaiTokenArtefact["networks"][REACT_NETWORK_ID]["address"].toLowerCase()] = DaiTokenArtefact.abi;
}
if (UsdcTokenArtefact["networks"][REACT_NETWORK_ID]["address"]) {
    TOKEN_ABI_MAP[UsdcTokenArtefact["networks"][REACT_NETWORK_ID]["address"].toLowerCase()] = UsdcTokenArtefact.abi;
}
if (CarbonArtefact["networks"][REACT_NETWORK_ID]["address"]) {
    TOKEN_ABI_MAP[CarbonArtefact["networks"][REACT_NETWORK_ID]["address"].toLowerCase()] = CarbonArtefact.abi;
}
if (ErgonArtefact["networks"][REACT_NETWORK_ID]["address"]) {
    TOKEN_ABI_MAP[ErgonArtefact["networks"][REACT_NETWORK_ID]["address"].toLowerCase()] = ErgonArtefact.abi;
}
// --- Mainnet address mapping for price lookup ---
const MAINNET_TOKEN_ADDRESS_MAP = {
    // Replace these with your local/testnet addresses as needed:
    // DAI
    [DaiTokenArtefact["networks"][REACT_NETWORK_ID]["address"]?.toLowerCase()]: "0x6b175474e89094c44da98b954eedeac495271d0f",
    // USDC
    [UsdcTokenArtefact["networks"][REACT_NETWORK_ID]["address"]?.toLowerCase()]: "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48",
    // ...add more as needed
};

export default function App() {
    const [theme, setTheme] = useState(() => {
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
            return savedTheme;
        }
        return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    });
    
    let buggyAccount = ''; // Placeholder for buggy account logic, if needed

    const [account, setAccount] = useState('');
    const [provider, setProvider] = useState(null);
    const [signer, setSigner] = useState(null);
    const [dexContract, setDexContract] = useState(null);
    const [balances, setBalances] = useState({});
    const [form, setForm] = useState({
        side: 'buy',
        tokenIn: NATIVE_TOKEN.address,
        tokenOut: '',
        amountIn: '',
        amountOut: '',
        expiryDuration: 120, // Default expiry: 1 hour in seconds
    });
    const [orders, setOrders] = useState([]);
    const [currentTime, setCurrentTime] = useState(Math.floor(Date.now() / 1000));
    const [chainId, setChainId] = useState(null);
    const [isApproved, setIsApproved] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [matchResult, setMatchResult] = useState(null);
    // --- Toast Notification System ---
    const [toasts, setToasts] = useState([]);

    // --- Persist matchedOrders in localStorage ---
    const [matchedOrders, setMatchedOrders] = useState(() => {
        // Load from localStorage if available
        try {
            const saved = localStorage.getItem('matchedOrders');
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });
    // Persist matchedOrders to localStorage whenever it changes
    useEffect(() => {
        try {
            localStorage.setItem('matchedOrders', JSON.stringify(matchedOrders));
        } catch {}
    }, [matchedOrders]);

    const [alert, setAlert] = useState({ isOpen: false, message: '', type: '' });
    const [isAdmin, setIsAdmin] = useState(false);

    // Add a toast
    const addToast = useCallback((message, type = 'info', duration = 5000) => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, duration);
    }, []);

    // Toast panel component
    const ToastPanel = () => (
        <aside className="toast-panel">
            {toasts.map(t => (
                <div key={t.id} className={`toast toast-${t.type}`}>{t.message}</div>
            ))}
        </aside>
    );

    useEffect(() => {
        document.body.className = theme;
        localStorage.setItem('theme', theme);
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
    };

    // Add allowedTokens state and dynamic tokens array
    const [allowedTokens, setAllowedTokens] = useState([]);
    const [tokens, setTokens] = useState([NATIVE_TOKEN]);

    const tokenMap = new Map(tokens.map(t => [t.address.toLowerCase(), t]));

    const fetchOrders = useCallback(async () => {
        if (IS_UI_ONLY) {
            console.log("UI ONLY MODE: Using mock orders.");
            const mockAccount = '0x1234567890123456789012345678901234567890';
            setOrders([
                { id: 'mock-1', trader: mockAccount, side: 'buy', tokenIn: tokens[0]?.address, tokenOut: tokens[1]?.address, amountIn: ethers.parseUnits('100', 18).toString(), amountOut: ethers.parseUnits('98', 18).toString(), nonce: 1, expiry: Math.floor(Date.now() / 1000) + 3600 },
                { id: 'mock-2', trader: '0xOtherTrader', side: 'sell', tokenIn: tokens[1]?.address, tokenOut: tokens[0]?.address, amountIn: ethers.parseUnits('50', 18).toString(), amountOut: ethers.parseUnits('51', 18).toString(), nonce: 1, expiry: Math.floor(Date.now() / 1000) + 3600 },
            ]);
            return;
        }
        try {
            const res = await axios.get(`${API_URL}/orders`);
            setOrders(res.data);
        } catch (error) {
            console.error("Failed to fetch orders:", error);
        }
    }, []);

    const fetchBalances = useCallback(async () => {
        if (IS_UI_ONLY) {
            console.log("UI ONLY MODE: Using mock balances.");
            setBalances({ 'TUSD': '1000', 'Carbon': '500', 'Ergon': '2000', 'ETH': '1000' });
            return;
        }
        if (!signer || !account || !provider || tokens.length === 0) return;
        // Debug: Log provider and account before fetching balances
        try {
            if (provider) {
                const network = await provider.getNetwork();
                console.log('[fetchBalances] Provider network:', network);
            }
        } catch (e) {
            console.error('[fetchBalances] Error fetching provider network:', e);
        }
        console.log('[fetchBalances] account:', account, 'provider:', provider);
        const newBalances = {};
        for (const token of tokens) {
            try {
                if (!account || !ethers.isAddress(account)) {
                    console.warn('Skipping getBalance: invalid account', account);
                    continue;
                }
                if (token.address === NATIVE_TOKEN.address) {
                    // Native ETH balance using direct RPC provider (never use injected provider for native ETH)
                    try {
                        // Use the same logic as in checkOwner.js
                        const rpcProvider = new ethers.JsonRpcProvider("https://serenity.ing/rpc");
                        const balance = await rpcProvider.getBalance(account);
                        const decimals = (typeof token.decimals === 'number' && !isNaN(token.decimals)) ? token.decimals : 18;
                        newBalances[token.name] = ethers.formatUnits(balance, decimals);
                    } catch (err) {
                        console.error('[fetchBalances] Failed to fetch native ETH balance via RPC provider:', err);
                        newBalances[token.name] = "Error";
                    }
                } else if (token.address && ethers.isAddress(token.address)) {
                    try {
                        const tokenContract = new ethers.Contract(token.address, IERC20Abi, provider);
                        const balance = await tokenContract.balanceOf(account);
                        const decimals = (typeof token.decimals === 'number' && !isNaN(token.decimals)) ? token.decimals : 18;
                        newBalances[token.name] = ethers.formatUnits(balance, decimals);
                    } catch (err) {
                        // Suppress errors for tokens not deployed on the current network
                        if (err.message && err.message.match(/call revert|execution reverted|missing revert data|no code/i)) {
                            console.warn(`[fetchBalances] Token ${token.name} not deployed on this network, skipping.`);
                            newBalances[token.name] = "N/A";
                        } else {
                            console.error(`Failed to fetch balance for ${token.name}`,
                                err,
                                { account, provider, token });
                            newBalances[token.name] = "Error";
                        }
                    }
                } else {
                    console.warn('Skipping token with invalid address:', token);
                    newBalances[token.name] = "Error";
                }
            } catch (error) {
                console.error(`Failed to fetch balance for ${token.name}`,
                    error,
                    { account, provider, token });
                newBalances[token.name] = "Error";
            }
        }
        setBalances(newBalances);
    }, [account, provider, signer]);

    useEffect(() => {
        const checkAllowance = async () => {
            if (IS_UI_ONLY || !signer || !form.tokenIn || !form.amountIn || !tokenMap.has(form.tokenIn.toLowerCase())) {
                setIsApproved(false);
                return;
            }
            // Skip allowance check for native ETH
            if (form.tokenIn.toLowerCase() === NATIVE_TOKEN.address.toLowerCase()) {
                setIsApproved(true);
                return;
            }
            try {
                const tokenInContract = new ethers.Contract(form.tokenIn, IERC20Abi, signer);
                const allowance = await tokenInContract.allowance(account, DEX_CONTRACT_ADDRESS);
                const tokenInInfo = tokenMap.get(form.tokenIn.toLowerCase());
                const amountInWei = ethers.parseUnits(form.amountIn || "0", tokenInInfo.decimals);

                setIsApproved(allowance >= amountInWei);
            } catch (error) {
                console.error("Failed to check allowance:", error);
                setIsApproved(false);
            }
        };

        checkAllowance();
    }, [signer, account, form.tokenIn, form.amountIn, tokenMap]);


    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(Math.floor(Date.now() / 1000));
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Effect for fetching initial orders
    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    // --- Keep tokens and tokenMap in refs for always-fresh access in event handlers ---
    const tokensRef = React.useRef(tokens);
    const tokenMapRef = React.useRef(tokenMap);
    useEffect(() => {
        tokensRef.current = tokens;
        tokenMapRef.current = tokenMap;
    }, [tokens, tokenMap]);

    // Effect for WebSocket connection
    useEffect(() => {
        if (IS_UI_ONLY) return; // Disable WebSocket in UI-only mode

        const ws = new WebSocket(WS_URL);

        ws.onmessage = (event) => {
            const message = JSON.parse(event.data);
            console.log('[WebSocket] Received message:', message);
            if (!message || !message.type) {
                console.warn('[WebSocket] Unknown message format:', message);
                return;
            }
            console.log(`[WebSocket] Handling message type: ${message.type}`);
            // Feed all status/progress messages to the toast system
            if (message.type === 'orders') {
                setOrders(message.data);
            } else if (message.type === 'match_success') {
                addToast('Trade matched and executed!', 'success');
                const tradeData = message.data;
                setMatchResult(tradeData);

                // lost account BUG !!!
                

                // --- New logic: determine user role (buy/sell) for order history ---
                const { buyOrder, sellOrder } = tradeData;
                let userRole = null;
                if (buggyAccount && buyOrder && buyOrder.trader && buggyAccount.toLowerCase() === buyOrder.trader.toLowerCase()) {
                    userRole = 'buy';
                } else if (buggyAccount && sellOrder && sellOrder.trader && buggyAccount.toLowerCase() === sellOrder.trader.toLowerCase()) {
                    userRole = 'sell';
                }
                // Only add to history if user is involved
                if (userRole && buyOrder && sellOrder) {
                    function getSymbolForAddress(address) {
                        if (!address) return '?';
                        const lower = address.toLowerCase();
                        let symbol = '';
                        const tokenInfo = tokenMapRef.current.get(lower);
                        if (tokenInfo && tokenInfo.symbol && tokenInfo.symbol.trim().length > 0) symbol = tokenInfo.symbol;
                        if (!symbol) {
                            const tokenObj = tokensRef.current.find(t => t.address.toLowerCase() === lower);
                            if (tokenObj && tokenObj.symbol && tokenObj.symbol.trim().length > 0) symbol = tokenObj.symbol;
                        }
                        if (!symbol && address.length > 8) symbol = address.slice(0, 5) + '...' + address.slice(-3);
                        if (!symbol) symbol = '?';
                        if (symbol.toUpperCase().endsWith(' Token')) symbol = symbol.slice(0, -6).trim();
                        return symbol;
                    }
                    // Fix: For buy, show the token and amount received (tokenOut), for sell, show token and amount sold (tokenIn)
                    const tokenInSymbol = getSymbolForAddress(buyOrder.tokenIn);
                    const tokenOutSymbol = getSymbolForAddress(buyOrder.tokenOut);
                    const tokenInInfo = tokenMapRef.current.get(buyOrder.tokenIn.toLowerCase());
                    const tokenOutInfo = tokenMapRef.current.get(buyOrder.tokenOut.toLowerCase());
                    let text = '';
                    if (userRole === 'buy') {
                        text = `Bought ${formatDisplayValue(buyOrder.amountOut, tokenOutInfo?.decimals || 18, 4)} ${tokenOutSymbol} for ${formatDisplayValue(buyOrder.amountIn, tokenInInfo?.decimals || 18, 4)} ${tokenInSymbol}`;
                    } else if (userRole === 'sell') {
                        text = `Sold ${formatDisplayValue(sellOrder.amountIn, tokenInInfo?.decimals || 18, 4)} ${tokenInSymbol} for ${formatDisplayValue(sellOrder.amountOut, tokenOutInfo?.decimals || 18, 4)} ${tokenOutSymbol}`;
                    }
                    setMatchedOrders(prev => [
                        {
                            id: tradeData.txHash || `trade-${Date.now()}`,
                            text,
                            timestamp: new Date().toLocaleTimeString(),
                            side: userRole // 'buy' or 'sell'
                        },
                        ...prev
                    ]);
                }
            } else if (message.type === 'seller_action_required') {
                const { buyOrder, buySig, sellOrder, sellSig } = message.data;
                if (buggyAccount && sellOrder && sellOrder.trader && buggyAccount.toLowerCase() === sellOrder.trader.toLowerCase()) {
                    console.log('[WebSocket] seller_action_required: Current user is seller, opening modal.', { buggyAccount, sellOrder });
                    setSellerMatchModal({
                        isOpen: true,
                        buyOrder,
                        buySig,
                        sellOrder,
                        sellSig,
                        message: 'A buyer has matched your ERC20/ETH order. Please send ETH to complete the trade.'
                    });
                } else {
                    console.log('[WebSocket] seller_action_required: Current user is NOT the seller, modal not shown.', { buggyAccount, sellOrder });
                }
            } else if (message.type === 'order_pending') {
                addToast('Order matching in progress...', 'info');
            } else if (message.type === 'error') {
                addToast(message.data?.message || 'An error occurred.', 'error');
            } else if (message.type === 'info') {
                addToast(message.data?.message || 'Info update.', 'info');
            } else if (message.type === 'success') {
                addToast(message.data?.message || 'Success.', 'success');
            } else if (message.type === 'warning') {
                addToast(message.data?.message || 'Warning.', 'warning');
            } else {
                console.warn('[WebSocket] Unhandled message type:', message.type, message);
            }
        };

        ws.onopen = () => console.log("WebSocket connected");
        ws.onclose = (event) => {
            console.log(`WebSocket disconnected: code=${event.code}, reason=${event.reason}`);
        };
        ws.onerror = (error) => console.error("WebSocket Error:", error);

        return () => {
            ws.close();
        };
    }, []); // <-- Empty dependency array ensures this runs only once on mount

    useEffect(() => {
        if (signer || IS_UI_ONLY) {
            fetchBalances();
        }
    }, [signer, fetchBalances]);

    const showAlert = (message, type = 'warning') => {
        addToast(message, type);
        // setAlert({ isOpen: true, message, type }); // Commented out: use toast system only
    };

    const closeAlert = () => {
        setAlert({ isOpen: false, message: '', type: '' });
    };

    const connectWallet = useCallback(async () => {
        if (IS_UI_ONLY) {
            console.log("UI ONLY MODE: Faking wallet connection.");
            setAccount('0x1234567890123456789012345678901234567890');
            buggyAccount = '0x1234567890123456789012345678901234567890'; // Placeholder for buggy account logic
            return;
        }

        if (!window.ethereum) {
            showAlert("Please install MetaMask!", 'error');
            return;
        }
        try {
            const p = new ethers.BrowserProvider(window.ethereum);
            setProvider(p);

            const [addr] = await p.send('eth_requestAccounts', []);
            setAccount(addr);
            buggyAccount = addr; // Placeholder for buggy account logic

            const s = await p.getSigner();
            setSigner(s);

            const contract = new ethers.Contract(DEX_CONTRACT_ADDRESS, SimpleDEXAbi, s);
            setDexContract(contract);

        } catch (err) {
            // Gracefully handle user rejection
            if (err.code === 4001) {
                console.log("User rejected connection.");
                return;
            }
            console.error(err);
            showAlert('Failed to connect wallet.', 'error');
        }
    }, []);

    useEffect(() => {
        // This effect handles wallet events like account or chain changes.
        if (window.ethereum) {
            const handleAccountsChanged = (accounts) => {
                if (accounts.length === 0) {
                    // Wallet is disconnected or locked.
                    //setAccount('');
                    setSigner(null);
                    setDexContract(null);
                    setBalances({});
                    showAlert('Wallet disconnected. Please connect again to continue.', 'warning');
                } else if (account && accounts[0].toLowerCase() !== account.toLowerCase()) {
                    // A new account is selected.
                    connectWallet(); // Re-connect with the new account.
                }
            };

            const handleChainChanged = () => {
                // For simplicity, reload the page on chain change.
                window.location.reload();
            };

            window.ethereum.on('accountsChanged', handleAccountsChanged);
            window.ethereum.on('chainChanged', handleChainChanged);

            // Cleanup listeners on component unmount
            return () => {
                if (window.ethereum.removeListener) {
                    window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
                    window.ethereum.removeListener('chainChanged', handleChainChanged);
                }
            };
        }
    }, [account, connectWallet]);


    async function approveToken() {
        if (!signer || !form.tokenIn) {
            showAlert("Cannot approve: Token not set.", 'warning');
            return;
        }
        setIsApproving(true);
        try {
            const tokenInContract = new ethers.Contract(form.tokenIn, IERC20Abi, signer);
            // Approve a large amount (MaxUint256) to avoid repeated approvals and streamline future trades
            const tx = await tokenInContract.approve(DEX_CONTRACT_ADDRESS, ethers.MaxUint256);
            console.log(`Approval transaction sent: ${tx.hash}`);
            await tx.wait();
            console.log(`Approval confirmed.`);
            setIsApproved(true);
            showAlert('Token approval successful!', 'confirmation');
        } catch (err) {
            if (err.code === 4001) {
                console.log("User rejected approval transaction.");
                showAlert("You cancelled the approval process.", 'warning');
                return;
            }
            console.error("Approval failed:", err);
            showAlert(`Approval failed: ${err.reason || err.message}`, 'error');
        } finally {
            setIsApproving(false);
        }
    }


    function handleChange(e) {
        setForm({ ...form, [e.target.name]: e.target.value });
    }

    async function createOrder() {
        if (IS_UI_ONLY) {
            const newOrder = {
                ...form,
                id: `mock-${Date.now()}`,
                trader: account,
                nonce: Math.floor(Math.random() * 10000),
            };
            console.log("UI ONLY MODE: Creating mock order", newOrder);
            setOrders(prev => [...prev, newOrder]);
            showAlert("Mock order created!", 'confirmation');
            return;
        }

        if (!dexContract || !account || !signer || !provider) {
            showAlert("Please connect wallet first", 'warning');
            return;
        }

        try {
            if (form.tokenIn === form.tokenOut) {
                showAlert("Token In and Token Out cannot be the same.", 'warning');
                return;
            }

            const tokenIn = tokenMap.get(form.tokenIn.toLowerCase());
            const tokenOut = tokenMap.get(form.tokenOut.toLowerCase());

            if (!tokenIn || !tokenOut) {
                showAlert("Invalid token selection.", 'warning');
                return;
            }

            const { chainId } = await provider.getNetwork();

            const { amountIn, amountOut, tokenIn: tokenInAddress, tokenOut: tokenOutAddress } = form;

            if (!amountIn || !amountOut || !tokenInAddress || !tokenOutAddress) {
              showAlert('Please fill all fields', 'warning');
              return;
            }

            const amountInWei = ethers.parseUnits(amountIn, 18);
            const amountOutWei = ethers.parseUnits(amountOut, 18);

            //const expiry = BigInt(Math.floor(Date.now() / 1000) + 3600); // 1 hour from now
            const expiry = BigInt(Math.floor(Date.now() / 1000) + parseInt(form.expiryDuration, 10));
            const nonce = BigInt(Date.now().toString() + Math.floor(Math.random() * 1000000).toString());

            let candidateNonce = nonce;
            // set a really unic nonce:
            while (await dexContract.isNonceUsed(account, candidateNonce)) {
                candidateNonce += 1n; // try next
            }

            // Correctly structured object for signing with BigInts for uint256 values
            const orderData = {
              trader: account,
              tokenIn: tokenInAddress,
              tokenOut: tokenOutAddress,
              amountIn: amountInWei,
              amountOut: amountOutWei,
              expiry: expiry,
              nonce: candidateNonce,
            };

            const domain = {
              name: 'ErgonDEX',
              version: '1',
              chainId: chainId,
              verifyingContract: DEX_CONTRACT_ADDRESS,
            };

            const types = {
                Order: [
                    { name: 'trader', type: 'address' },
                    { name: 'tokenIn', type: 'address' },
                    { name: 'tokenOut', type: 'address' },
                    { name: 'amountIn', type: 'uint256' },
                    { name: 'amountOut', type: 'uint256' },
                    { name: 'expiry', type: 'uint256' },
                    { name: 'nonce', type: 'uint256' },
                ]
      };

      // Create the order data with BigInts for signing
      const orderDataForSigning = {
        trader: account,
        tokenIn: tokenInAddress,
        tokenOut: tokenOutAddress,
        amountIn: amountInWei,
        amountOut: amountOutWei,
        expiry: expiry,
        nonce: candidateNonce,
      };

      // Calculate the hash on the frontend for debugging
      const frontendHash = ethers.TypedDataEncoder.hash(domain, types, orderDataForSigning);
      console.log("Frontend calculated hash for signing:", frontendHash);

      // Sign the typed data
      const signature = await signer.signTypedData(domain, types, orderDataForSigning);

            // Convert BigInts to strings for JSON serialization before sending to the backend
            const orderForBackend = {
                trader: account,
                tokenIn: tokenInAddress,
                tokenOut: tokenOutAddress,
                amountIn: amountInWei.toString(),
                amountOut: amountOutWei.toString(),
                expiry: expiry.toString(),
                nonce: candidateNonce.toString(),
                signature,
                side: form.side,
                frontendHash: frontendHash, // Include the hash for debugging on the backend
            };

            const response = await fetch(`${API_URL}/orders`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(orderForBackend),
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.message || `HTTP error! status: ${response.status}`);
            }

            showAlert('Order submitted successfully!', 'confirmation');
            await response.json();
        } catch (e) {
            if (e.code === 'ACTION_REJECTED' || e.code === 4001) {
                console.log('User rejected transaction signature.');
                showAlert('You cancelled the order creation process.', 'warning');
            } else {
                console.error('Error creating order:', e);
                showAlert(`Error creating order: ${e.message}`, 'error');
            }
        }
    }

    // Helper to get token name or a fallback
    const getTokenName = (address) => {
    if (!address) return '';
    if (address.toLowerCase() === NATIVE_TOKEN.address.toLowerCase()) return NATIVE_TOKEN.name;
    const token = tokenMap.get(address?.toLowerCase?.() || '');
    if (token && token.name) {
        let name = token.name;
        // Trim both ' Token' and ' token' (case-insensitive)
        if (name.toUpperCase().endsWith(' TOKEN')) name = name.slice(0, -6).trim();
        if (name.toLowerCase().endsWith(' token')) name = name.slice(0, -6).trim();
        return name;
    }
    return address ? `${address.slice(0, 5)}...${address.slice(-3)}` : '';
    };

    // Helper to get token symbol or fallback
    const getTokenSymbol = (tokenOrAddress) => {
        if (!tokenOrAddress) return '';
        let address = typeof tokenOrAddress === 'string' ? tokenOrAddress : tokenOrAddress.address;
        if (address && address.toLowerCase() === NATIVE_TOKEN.address.toLowerCase()) return NATIVE_TOKEN.name;
        if (typeof tokenOrAddress === 'object' && tokenOrAddress.symbol) return tokenOrAddress.symbol;
        const token = tokenMap.get(address?.toLowerCase?.() || '');
        return token ? (token.symbol || token.name) : (address ? address.slice(0, 6) : '');
    };

    // Helper to get canonical pair key (address-based)
    const getPairKey = React.useCallback(() => {
        if (!form.tokenIn || !form.tokenOut) return '';
        return `${form.tokenIn}/${form.tokenOut}`;
    }, [form.tokenIn, form.tokenOut]);

    // Memoize pair name for display (symbol-based)
    const pairName = React.useMemo(() => {
        return `${getTokenName(form.tokenIn)}/${getTokenName(form.tokenOut)}`;
    }, [form.tokenIn, form.tokenOut, getTokenName]);

    // New handler to pre-fill the form when an order is clicked
    const handleOrderClick = (order) => {
        const price = calculatePrice(order);
        const tokenIn = tokenMap.get(order.tokenOut.toLowerCase()); // Opposite token
        const tokenOut = tokenMap.get(order.tokenIn.toLowerCase()); // Opposite token

        if (!tokenIn || !tokenOut) return;

        // --- Balance check before pre-filling form ---
        let requiredAmount = order.side === 'buy'
            ? ethers.formatUnits(order.amountOut, tokenIn.decimals)
            : ethers.formatUnits(order.amountIn, tokenIn.decimals);
        let userBalance = balances[tokenIn.name] || '0';
        if (parseFloat(userBalance) < parseFloat(requiredAmount)) {
            showAlert(`Insufficient balance to match this order. Required: ${requiredAmount} ${tokenIn.symbol || tokenIn.name}, Available: ${userBalance}`, 'error');
            return;
        }

        setForm({
            side: order.side === 'buy' ? 'sell' : 'buy', // Opposite side
            tokenIn: order.tokenOut, // Swap tokens
            tokenOut: order.tokenIn,
            amountIn: ethers.formatUnits(order.amountOut, tokenIn.decimals), // Swap amounts
            amountOut: ethers.formatUnits(order.amountIn, tokenOut.decimals),
            expiryDuration: 120,
        });
    };

    // Helper to check if a token is native ETH pseudo-token
    function isNativeETH(address) {
        return address && address.toLowerCase() === NATIVE_TOKEN.address.toLowerCase();
    }

    // Call this when a user wants to match an order
    async function handleMatchOrder(buyOrder, buySig, sellOrder, sellSig) {
        // Detect pair type
        const buyInETH = isNativeETH(buyOrder.tokenIn);
        const sellInETH = isNativeETH(sellOrder.tokenIn);

        // ERC20/ERC20 or ETH/ERC20 (buyer pays ETH)
        if (!sellInETH) {
            // Anyone can call, including backend/worker or frontend
            let txOpts = {};
            if (buyInETH) {
                txOpts.value = buyOrder.amountIn; // Send ETH with tx
            }
            try {

                console.log('account:', account);
                console.log('sellOrder.trader:', sellOrder.trader);
                console.log('buyOrder.trader:', buyOrder.trader);
                console.log('sellOrder:', sellOrder);

                const tx = await dexContract.matchOrders(
                    buyOrder,
                    buySig,
                    sellOrder,
                    sellSig,
                    txOpts
                );
                await tx.wait();
                showAlert('Trade executed!', 'success');
            } catch (e) {
                showAlert(`Error executing trade: ${e.message}`, 'error');
            }
            return;
        }

        // ERC20/ETH (seller pays ETH)
        // Only the seller can call, and must send ETH
        if (
            account &&
            account.toLowerCase() === sellOrder.trader.toLowerCase()
        ) {
            try {
                // --- Log raw orders and account before normalization ---
                console.log('[SELLER ETH SEND] RAW account:', account);
                console.log('[SELLER ETH SEND] RAW sellOrder:', sellOrder);
                console.log('[SELLER ETH SEND] RAW buyOrder:', buyOrder);
                // --- Normalize all order fields to BigInt and log for debugging ---
                const normOrder = (order) => ({
                    trader: ethers.getAddress(order.trader),
                    tokenIn: ethers.getAddress(order.tokenIn),
                    tokenOut: ethers.getAddress(order.tokenOut),
                    amountIn: typeof order.amountIn === 'string' ? BigInt(order.amountIn) : order.amountIn,
                    amountOut: typeof order.amountOut === 'string' ? BigInt(order.amountOut) : order.amountOut,
                    expiry: typeof order.expiry === 'string' ? BigInt(order.expiry) : order.expiry,
                    nonce: typeof order.nonce === 'string' ? BigInt(order.nonce) : order.nonce,
                });
                const buyOrderNorm = normOrder(buyOrder);
                const sellOrderNorm = normOrder(sellOrder);
                const valueToSend = sellOrderNorm.amountIn;
                // --- Detailed logging of types and values ---
                console.log('[SELLER ETH SEND] account:', account);
                console.log('[SELLER ETH SEND] sellOrderNorm:', sellOrderNorm);
                Object.entries(sellOrderNorm).forEach(([k, v]) => {
                    console.log(`[SELLER ETH SEND] sellOrderNorm field`, k, v, typeof v);
                });
                console.log('[SELLER ETH SEND] buyOrderNorm:', buyOrderNorm);
                Object.entries(buyOrderNorm).forEach(([k, v]) => {
                    console.log(`[SELLER ETH SEND] buyOrderNorm field`, k, v, typeof v);
                });
                console.log('[SELLER ETH SEND] valueToSend:', valueToSend, typeof valueToSend);
                const tx = await dexContract.matchOrders(
                    buyOrderNorm,
                    buySig,
                    sellOrderNorm,
                    sellSig,
                    { value: valueToSend }
                );
                await tx.wait();
                showAlert('Trade executed! ETH sent and ERC20 received.', 'success');
                setSellerMatchModal(m => ({ ...m, isOpen: false })); // Close modal on success
                // Add to order history here for ERC20/ETH after seller confirms
                function getSymbolForAddress(address) {
                    if (!address) return '?';
                    const lower = address.toLowerCase();
                    let symbol = '';
                    const tokenInfo = tokenMap.get(lower);
                    if (tokenInfo && tokenInfo.symbol && tokenInfo.symbol.trim().length > 0) symbol = tokenInfo.symbol;
                    if (!symbol) {
                        const tokenObj = tokens.find(t => t.address.toLowerCase() === lower);
                        if (tokenObj && tokenObj.symbol && tokenObj.symbol.trim().length > 0) symbol = tokenObj.symbol;
                    }
                    if (!symbol && address.length > 8) symbol = address.slice(0, 5) + '...' + address.slice(-3);
                    if (!symbol) symbol = '?';
                    // Trim both ' Token' and ' token' (case-insensitive)
                    if (symbol.toUpperCase().endsWith(' TOKEN')) symbol = symbol.slice(0, -6).trim();
                    if (symbol.toLowerCase().endsWith(' token')) symbol = symbol.slice(0, -6).trim();
                    return symbol;
                }
                const tokenInSymbol = getSymbolForAddress(sellOrderNorm.tokenIn);
                const tokenOutSymbol = getSymbolForAddress(sellOrderNorm.tokenOut);
                const tokenInInfo = tokenMap.get(sellOrderNorm.tokenIn.toLowerCase());
                const tokenOutInfo = tokenMap.get(sellOrderNorm.tokenOut.toLowerCase());
                const text =  `Sold ${formatDisplayValue(sellOrderNorm.amountIn, tokenInInfo?.decimals || 18, 2)} ${tokenInSymbol} for ${formatDisplayValue(sellOrderNorm.amountOut, tokenOutInfo?.decimals || 18, 2)} ${tokenOutSymbol}`;
                setMatchedOrders(prev => [
                    {
                        id: `trade-${Date.now()}`,
                        text,
                        timestamp: new Date().toLocaleTimeString(),
                        side: sellOrderNorm.side
                    },
                    ...prev
                ]);
            } catch (e) {
                console.error('[SELLER ETH SEND ERROR]', e);
                showAlert(`Error executing trade: ${e.message}`, 'error');
            }
        } else {
            showAlert('Only the seller can execute this trade by sending ETH. Please wait for the seller to act.', 'warning');
        }
        
    }


    async function cancelOrder(orderId) {
        if (!dexContract || !account || !signer) {
            showAlert("Please connect wallet first", 'warning');
            return;
        }

        const orderToCancel = orders.find(o => o.id === orderId);
        if (!orderToCancel) {
            showAlert("Order not found. It may have already been matched or cancelled.", 'warning');
            fetchOrders(); // Refresh the list
            return;
        }

        console.log("Cancelling order:", orderToCancel);

        try {
            // Reconstruct the order object for the contract call
            const orderParams = {
                trader: orderToCancel.trader,
                tokenIn: orderToCancel.tokenIn,
                tokenOut: orderToCancel.tokenOut,
                amountIn: orderToCancel.amountIn,
                amountOut: orderToCancel.amountOut,
                expiry: orderToCancel.expiry,
                nonce: orderToCancel.nonce
            };

            const tx = await dexContract.cancelOrder(orderParams);
            console.log(`Cancellation transaction sent: ${tx.hash}`);
            await tx.wait();
            console.log(`Cancellation confirmed for order: ${orderToCancel.id}`);
            showAlert('Order cancelled successfully!', 'confirmation');

            // Notify backend to remove the order from the book
            await axios.post(`${API_URL}/orders/cancel`, { orderId: orderToCancel.id });

            // No need for alert, UI will update via WebSocket
            fetchOrders(); // Manually refresh as a fallback
        } catch (err) {
            // Gracefully handle user rejection
            if (err.code === 4001) {
                console.log("User rejected cancellation transaction.");
                showAlert("You cancelled the order cancellation process.", 'warning');
                return;
            }
            console.error("Error cancelling order:", err);
            showAlert(`Error cancelling order: ${err.reason || err.message}`, 'error');
        }
    }

    const formatTime = (seconds) => {
        if (seconds <= 0) return "Expired";
        const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
        const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${h}:${m}:${s}`;
    };

    const availableTokensForTokenOut = tokens.filter(t => t.address !== form.tokenIn);

    const calculatePrice = (order) => {
        // Correctly calculate price using amountIn and amountOut
        if (!order || !order.amountIn || !order.amountOut || BigInt(order.amountIn) === 0n) {
            return 0n;
        }
        try {
            // Price = amountOut / amountIn. Multiply by 1e18 for precision.
            return (BigInt(order.amountOut) * 10n**18n) / BigInt(order.amountIn);
        } catch (e) {
            console.error("Could not calculate price for order:", order, e);
            return 0n;
        }
    };

    const buyOrders = orders
        .filter(o => o.side === 'buy')
        .sort((a, b) => {
            const priceA = calculatePrice(a);
            const priceB = calculatePrice(b);
            // For buy orders, highest price is best
            if (priceA < priceB) return 1;
            if (priceA > priceB) return -1;
            return 0;
        });

    const sellOrders = orders
        .filter(o => o.side === 'sell')
        .sort((a, b) => {
            const priceA = calculatePrice(a);
            const priceB = calculatePrice(b);
            // For sell orders, lowest price is best
            if (priceA > priceB) return 1;
            if (priceA < priceB) return -1;
            return 0;
        });

    const formatDisplayValue = (value, decimals = 18, displayDecimals = 8) => {
        try {
            const formatted = ethers.formatUnits(value, decimals);
            const num = parseFloat(formatted);
            if (isNaN(num)) return (0).toFixed(displayDecimals);
            return num.toFixed(displayDecimals);
        } catch (e) {
            console.error("Formatting error", e);
            return (0).toFixed(displayDecimals);
        }
    };

    const selectedTokenInfo = form.tokenIn ? tokenMap.get(form.tokenIn.toLowerCase()) : null;
    const selectedTokenBalance = selectedTokenInfo ? (balances[selectedTokenInfo.name] || '0.00') : '0.00';
    let selectedTokenName = selectedTokenInfo ? selectedTokenInfo.name : '';
    if (selectedTokenName.toUpperCase().endsWith(' TOKEN')) selectedTokenName = selectedTokenName.slice(0, -6).trim();

    const getAlertIcon = () => {
        switch (alert.type) {
            case 'error':
                return <span className="alert-dialog-icon error">&times;</span>; // Heavy multiplication sign
            case 'confirmation':
                return <span className="alert-dialog-icon confirmation">&#10003;</span>; // Checkmark
            case 'warning':
            default:
                return <span className="alert-dialog-icon warning">!</span>; // Exclamation mark
        }
    };

    // Track previous orders for expiry toast
    const prevOrdersRef = React.useRef();
    useEffect(() => {
        // Only run after initial mount
        if (!prevOrdersRef.current?.length) {
            prevOrdersRef.current = orders;
            return;
        }
        const prevOrders = prevOrdersRef.current;
        const prevOrderMap = new Map(prevOrders.map(o => [o.id, o]));
        const now = Math.floor(Date.now() / 1000);
        // Find orders that were present before, now missing, and expired
        prevOrders.forEach(o => {
            if (!orders.find(n => n.id === o.id) && (o.expiry - now) <= 0) {
                addToast(`Order expired: ${getTokenName(o.tokenIn)} → ${getTokenName(o.tokenOut)} (${o.side})`, 'warning', 7000);
            }
        });
        prevOrdersRef.current = orders;
    }, [orders, addToast, getTokenName]);

    useEffect(() => {
        async function checkAdmin() {
            if (dexContract && account) {
                try {
                    // Use direct RPC provider for owner check to avoid MetaMask errors
                    const rpcProvider = new ethers.JsonRpcProvider("https://serenity.ing/rpc");
                    const contract = new ethers.Contract(DEX_CONTRACT_ADDRESS, SimpleDEXAbi, rpcProvider);
                    const owner = await contract.owner();
                    console.log('[Admin Check] Contract owner (via RPC):', owner, 'Current account:', account);
                    setIsAdmin(owner.toLowerCase() === account.toLowerCase());
                } catch (e) {
                    console.error('[Admin Check] Error fetching owner via RPC:', e);
                    setIsAdmin(false);
                }
            } else {
                setIsAdmin(false);
            }
        }
        checkAdmin();
    }, [dexContract, account]);

    // --- Material UI theme for admin dashboard ---
    const muiTheme = createTheme({ palette: { mode: theme } });

    // --- Utility: Fetch token info and price ---
const STABLECOIN_SYMBOLS = ['USDT', 'USDC', 'DAI', 'TUSD', 'BUSD', 'USDP', 'GUSD', 'USDD', 'LUSD', 'FRAX', 'USDE', 'EURT', 'EURS'];
const FALLBACK_PRICES = {
    'ERGON': 0.30,
    'CARBON': 0.02975,
    'TUSD': 1.0
};

async function getTokenInfo(tokenAddress, provider) {
    if (tokenAddress === NATIVE_TOKEN.address) {
        return { symbol: NATIVE_TOKEN.name, name: NATIVE_TOKEN.name, decimals: NATIVE_TOKEN.decimals };
    }
    try {
        const tokenContract = new ethers.Contract(tokenAddress, IERC20Abi, provider);
        const [symbol, name, decimals] = await Promise.all([
            tokenContract.symbol(),
            tokenContract.name(),
            tokenContract.decimals()
        ]);
        return { symbol, name, decimals };
    } catch (e) {
        return { symbol: '', name: '', decimals: 18 };
    }
}

function isStablecoin(symbol) {
    return STABLECOIN_SYMBOLS.includes(symbol.toUpperCase());
}

async function fetchTokenPrice(tokenAddress, symbol) {
    // Native ETH: fetch from CoinGecko or set custom price for private network
    if (tokenAddress === NATIVE_TOKEN.address) {
        // Try CoinGecko, fallback to 0 for private network
        try {
            const url = 'https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd';
            console.log('[CoinGecko] Querying ETH price:', { url });
            const res = await fetch(url);
            const data = await res.json();
            const price = data.ethereum?.usd || 0;
            console.log('[CoinGecko] ETH price result:', { data, price });
            return price;
        } catch (err) {
            console.error('[CoinGecko] Error fetching ETH price:', { err });
            return 0;
        }
    }
    // Fallback for Ergon/Carbon
    if (symbol && FALLBACK_PRICES[symbol.toUpperCase()]) {
        return FALLBACK_PRICES[symbol.toUpperCase()];
    }
    // Use mainnet address for CoinGecko if mapped
    const coingeckoAddress = MAINNET_TOKEN_ADDRESS_MAP[tokenAddress.toLowerCase()] || tokenAddress;
    try {
        const url = `https://api.coingecko.com/api/v3/simple/token_price/ethereum?contract_addresses=${coingeckoAddress}&vs_currencies=usd`;
        console.log('[CoinGecko] Querying token price:', { tokenAddress, coingeckoAddress, url });
        const res = await fetch(url);
        const data = await res.json();
        const price = data[coingeckoAddress.toLowerCase()]?.usd;
        console.log('[CoinGecko] Token price result:', { tokenAddress, coingeckoAddress, data, price });
        if (price) return price;
    } catch (err) {
        console.error('[CoinGecko] Error fetching token price:', { tokenAddress, coingeckoAddress, err });
    }
    // Fallback for unknown tokens
    return null;
}

// --- Add selectedTokenInfoExtra state and effect for token info/price ---
const [selectedTokenInfoExtra, setSelectedTokenInfoExtra] = useState({ symbol: '', isStable: false, price: null });

useEffect(() => {
    async function updateTokenInfo() {
        if (!form.tokenIn || !provider || !account) {
            setSelectedTokenInfoExtra({ symbol: '', isStable: false, price: null });
            return;
        }
        let info = { symbol: '', name: '', decimals: 18 };
        try {
            info = await getTokenInfo(form.tokenIn, provider);
        } catch {}
        // Always use symbol for display and logic
        let symbol = info.symbol || '';
        if (!symbol && form.tokenIn) {
            // Try to infer symbol from known addresses
            const token = tokens.find(t => t.address.toLowerCase() === form.tokenIn.toLowerCase());
            symbol = token ? token.symbol : '';
        }
        // Remove any trailing ' Token' from symbol (for Carbon/Ergon)
        //if (symbol && symbol.toUpperCase().endsWith(' TOKEN')) {
        if (symbol && symbol.toUpperCase().endsWith(' Token')) {
            symbol = symbol.slice(0, -6).trim();
        }
        const stable = isStablecoin(symbol);
        let price = await fetchTokenPrice(form.tokenIn, symbol);
        // Fallback for unknown tokens: use Ergon/Carbon/TUSD price if symbol matches
        if (price === null && symbol && FALLBACK_PRICES[symbol.toUpperCase()]) {
            price = FALLBACK_PRICES[symbol.toUpperCase()];
        }
        setSelectedTokenInfoExtra({ symbol, isStable: stable, price });
    }
    updateTokenInfo();
}, [form.tokenIn, provider, tokens, account]);

    // Supported tokens for price history chart
const SUPPORTED_CHART_TOKENS = [
    process.env.REACT_APP_DUMMY_TOKEN_ADDRESS?.toLowerCase(), // TUSD
    process.env.REACT_APP_CARBON_TOKEN_ADDRESS?.toLowerCase(), // Carbon
    process.env.REACT_APP_ERGON_TOKEN_ADDRESS?.toLowerCase(), // Ergon
    process.env.REACT_APP_DAI_TOKEN_ADDRESS?.toLowerCase(), // DAI
    process.env.REACT_APP_USDC_TOKEN_ADDRESS?.toLowerCase(), // USDC
];

function isChartSupported(tokenA, tokenB) {
    return SUPPORTED_CHART_TOKENS.includes(tokenA?.toLowerCase()) && SUPPORTED_CHART_TOKENS.includes(tokenB?.toLowerCase());
}

    useEffect(() => {
    async function fetchAllowedTokens() {
        if (!DEX_CONTRACT_ADDRESS) return;
        try {
            const rpcProvider = new ethers.JsonRpcProvider("https://serenity.ing/rpc");
            const contract = new ethers.Contract(DEX_CONTRACT_ADDRESS, SimpleDEXAbi, rpcProvider);
            const tokenCount = await contract.getTokenListLength();
            let tokensList = [];
            for (let i = 0; i < tokenCount; i++) {
                const addr = await contract.tokenList(i);
                tokensList.push(addr);
            }
            console.log('[App.js] allowedTokens from contract:', tokensList);
            setAllowedTokens(tokensList);
        } catch (e) {
            console.error('[App.js] Failed to fetch allowed tokens:', e);
            setAllowedTokens([]);
        }
    }
    fetchAllowedTokens();
}, []); // DEX_CONTRACT_ADDRESS is not a valid dependency

    useEffect(() => {
        // Always include symbol in tokens array
        // Always include native ETH
        const base = [{ ...NATIVE_TOKEN, symbol: NATIVE_TOKEN.name }];
        async function buildTokens() {
            // Always use static provider for metadata
            const ethersProvider = new ethers.JsonRpcProvider("https://serenity.ing/rpc");
            console.log('[buildTokens] Using JsonRpcProvider (static) for token metadata');
            // Fetch info for all allowed tokens (symbol, name, decimals)
            const tokenObjs = await Promise.all(allowedTokens.map(async (addr) => {
                if (addr.toLowerCase() === NATIVE_TOKEN.address.toLowerCase()) return { ...NATIVE_TOKEN, symbol: NATIVE_TOKEN.name };
                let symbol = '', name = '', decimals = 18;
                // --- Use correct ABI for each token ---
                const abi = TOKEN_ABI_MAP[addr.toLowerCase()] || IERC20Abi;
                try {
                    const token = new ethers.Contract(addr, abi, ethersProvider);
                    // Debug: Log the contract object and ABI used
                    console.log(`[buildTokens] token contract for ${addr} (ABI: ${abi === IERC20Abi ? 'ERC20' : 'custom'})`, token);
                    try {
                        symbol = await token.symbol();
                        console.log(`[buildTokens] symbol for ${addr}:`, symbol);
                    } catch (e) {
                        console.error(`[buildTokens] symbol() failed for ${addr}:`, e, { abi, addr, provider: ethersProvider.connection });
                    }
                    try {
                        name = await token.name();
                        console.log(`[buildTokens] name for ${addr}:`, name);
                    } catch (e) {
                        console.error(`[buildTokens] name() failed for ${addr}:`, e, { abi, addr, provider: ethersProvider.connection });
                    }
                    try {
                        decimals = await token.decimals();
                        console.log(`[buildTokens] decimals for ${addr}:`, decimals);
                    } catch (e) {
                        console.error(`[buildTokens] decimals() failed for ${addr}:`, e, { abi, addr, provider: ethersProvider.connection });
                    }
                    return {
                        name: name && name.length > 0 ? name : addr.slice(0, 6),
                        symbol: symbol && symbol.length > 0 ? symbol : addr.slice(0, 6),
                        address: addr,
                        decimals: Number(decimals) || 18
                    };
                } catch (e) {
                    console.error(`[buildTokens] First fetch failed for ${addr}:`, e, { abi, addr, provider: ethersProvider.connection });
                    // Try again with a new provider (network hiccup)
                    try {
                        const freshProvider = new ethers.JsonRpcProvider("https://serenity.ing/rpc");
                        const token = new ethers.Contract(addr, abi, freshProvider);
                        // Step-by-step logging and calls for retry
                        try {
                            console.log(`[buildTokens] [retry] Calling symbol() for ${addr}`);
                            symbol = await token.symbol();
                            console.log(`[buildTokens] [retry] symbol() for ${addr}:`, symbol);
                        } catch (e) {
                            console.error(`[buildTokens] [retry] symbol() failed for ${addr}:`, e, { abi, addr, provider: freshProvider.connection });
                        }
                        try {
                            console.log(`[buildTokens] [retry] Calling name() for ${addr}`);
                            name = await token.name();
                            console.log(`[buildTokens] [retry] name() for ${addr}:`, name);
                        } catch (e) {
                            console.error(`[buildTokens] [retry] name() failed for ${addr}:`, e, { abi, addr, provider: freshProvider.connection });
                        }
                        try {
                            console.log(`[buildTokens] [retry] Calling decimals() for ${addr}`);
                            decimals = await token.decimals();
                            console.log(`[buildTokens] [retry] decimals() for ${addr}:`, decimals);
                        } catch (e) {
                            console.error(`[buildTokens] [retry] decimals() failed for ${addr}:`, e, { abi, addr, provider: freshProvider.connection });
                        }
                        return {
                            name: name && name.length > 0 ? name : addr.slice(0, 6),
                            symbol: symbol && symbol.length > 0 ? symbol : addr.slice(0, 6),
                            address: addr,
                            decimals: Number(decimals) || 18
                        };
                    } catch (e2) {
                        console.error(`[buildTokens] Second fetch failed for ${addr}:`, e2, { abi, addr });
                        // Fallback: show short address as symbol and name
                        return { name: addr.slice(0, 6), symbol: addr.slice(0, 6), address: addr, decimals: 18 };
                    }
                }
            }));
            // Always include native ETH, and all allowed tokens with correct info
            const all = [...base, ...tokenObjs.filter(t => t.address.toLowerCase() !== NATIVE_TOKEN.address.toLowerCase())];
            const filtered = all.filter(Boolean);
            setTokens(filtered);
        }
        if (allowedTokens.length > 0) buildTokens();
        else setTokens(base);
    }, [allowedTokens]);

    // Auto-select form.tokenOut to the first available token not equal to form.tokenIn
    useEffect(() => {
    if (tokens.length < 2) return;
    // If tokenOut is not set or is the same as tokenIn, pick the first different token
    if (!form.tokenOut || form.tokenIn === form.tokenOut) {
        const firstOther = tokens.find(t => t.address !== form.tokenIn);
        if (firstOther) {
            setForm(f => ({ ...f, tokenOut: firstOther.address }));
        }
    }
}, [tokens, form.tokenIn, form.tokenOut]);

    // --- Seller Match Modal Component ---
    const SellerMatchModal = ({ isOpen, message, onClose, onConfirm, buyOrder, buySig, sellOrder, sellSig }) => {
        if (!isOpen) return null;
        return (
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-box" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Seller Action Required</h2>
                    </div>
                    <div className="modal-content">
                        <p>{message}</p>
                    </div>
                    <div className="modal-footer">
                        <button onClick={onClose}>Cancel</button>
                        <button onClick={() => onConfirm && onConfirm(buyOrder, buySig, sellOrder, sellSig)}>
                            Send ETH &amp; Complete Trade
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Helper to render an order row (buy or sell)
    function renderOrderRow(order, type, { account, tokenMap, addToast, cancelOrder, currentTime, formatDisplayValue, formatTime }) {
        const tokenInInfo = tokenMap.get(order.tokenIn.toLowerCase());
        const tokenOutInfo = tokenMap.get(order.tokenOut.toLowerCase());
        const tokenInName = getTokenName(order.tokenIn);
        const tokenOutName = getTokenName(order.tokenOut);
        let label = '';
        if (type === 'buy') {
            label = `Buy ${formatDisplayValue(order.amountOut, tokenOutInfo?.decimals || 18, 4)} ${tokenOutName} for ${formatDisplayValue(order.amountIn, tokenInInfo?.decimals || 18, 4)} ${tokenInName}`;
        } else {
            label = `Sell ${formatDisplayValue(order.amountIn, tokenInInfo?.decimals || 18, 4)} ${tokenInName} for ${formatDisplayValue(order.amountOut, tokenOutInfo?.decimals || 18, 4)} ${tokenOutName}`;
        }
        const isActive = (order.expiry - currentTime) > 0;
        return (
            <div key={order.id} className={`order-row ${type}-row`} onClick={() => handleOrderClick(order)}>
                <div className={`price ${type}-price`}>{label}</div>
                <div>{formatDisplayValue(order.amountIn, tokenInInfo?.decimals || 18)}</div>
                <div>{formatTime(order.expiry - currentTime)}</div>
                <div>
                    {account && order.trader.toLowerCase() === account.toLowerCase() && (
                        <button
                            className="cancel-order-btn"
                            disabled={!isActive}
                            title={!isActive ? 'Order is matched or expired' : 'Cancel order'}
                            onClick={(e) => {
                                if (!isActive) return;
                                e.stopPropagation(); // Prevent row click
                                addToast('Cancelling order...', 'info', 3500);
                                cancelOrder(order.id);
                            }}
                        >
                            &times;
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // --- Seller Match Modal State ---
    const [sellerMatchModal, setSellerMatchModal] = useState({
        isOpen: false,
        buyOrder: null,
        buySig: null,
        sellOrder: null,
        sellSig: null,
        message: ''
    });

    return (
        <ThemeProvider theme={muiTheme}>
        <div className={`App ${theme}`}>
            <header className="App-header">
                <h1 className="logo-text-color">ErgonDEX</h1>
                <div className="header-right">
                    <label className="switch">
                        <input type="checkbox" onChange={toggleTheme} checked={theme === 'dark'} />
                        <span className="slider round"></span>
                    </label>
                    <button onClick={connectWallet} className="wallet-button">
                        {account ? `Connected: ${account.slice(0, 6)}...${account.slice(-4)}` : 'Connect Wallet'}
                    </button>
                </div>
            </header>

            {alert.isOpen && (
                <div className="alert-dialog-overlay" onClick={closeAlert}>
                    <div className="alert-dialog-box" onClick={e => e.stopPropagation()}>
                        <div className="alert-dialog-header">
                            {getAlertIcon()}
                            <h2>{alert.type.charAt(0).toUpperCase() + alert.type.slice(1)}</h2>
                        </div>
                        <div className="alert-dialog-content">
                            <p>{alert.message}</p>
                        </div>
                        <div className="alert-dialog-footer">
                            <button onClick={closeAlert}>OK</button>
                        </div>
                    </div>
                </div>
            )}

            <ToastPanel />
            <ChatWidget />
            {/* --- Admin Dashboard --- */}
            {isAdmin && (
                <AdminDashboard isAdmin={isAdmin} dexContract={dexContract} account={account} provider={provider} />
            )}

            <main className="App-content">
                <div className="top-section">
                    <div className="create-order-wrapper">
                        <h2>Create Order</h2>
                        <form className="create-order-form" onSubmit={e => { e.preventDefault(); createOrder(); }}>
                            <div className="form-group">
                                <label>Side</label>
                                <select name="side" value={form.side} onChange={handleChange}>
                                    <option value="buy">Buy</option>
                                    <option value="sell">Sell</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Expiry (seconds)</label>
                                <input type="number" name="expiryDuration" value={form.expiryDuration} onChange={handleChange} />
                            </div>
                            <div className="form-group">
                                <label>Token to Spend</label>
                                <select name="tokenIn" value={form.tokenIn} onChange={handleChange}>
                                    {tokens.map(t => <option key={t.address} value={t.address}>{t.symbol && t.symbol.length > 0 ? t.symbol : t.address.slice(0, 6)}</option>)}
                                </select>
                                {account ? (
                                    <>
                                        {selectedTokenInfoExtra.price !== null && (
                                            <div className="token-info-extra">
                                                <span>Price: ${selectedTokenInfoExtra.price}</span>
                                            </div>
                                        )}
                                        {selectedTokenInfoExtra.price === null && !selectedTokenInfoExtra.isStable && (
                                            <div className="token-info-extra">
                                                <span>Fallback: ${FALLBACK_PRICES['ERGON']} (Ergon) / ${FALLBACK_PRICES['CARBON']} (Carbon)</span>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="token-info-extra">
                                        <span>Connect wallet to view token price</span>
                                    </div>
                                )}
                            </div>
                            <div className="form-group">
                                <label>Token to Receive</label>
                                <select name="tokenOut" value={form.tokenOut} onChange={handleChange}>
                                    {availableTokensForTokenOut.map(t => <option key={t.address} value={t.address}>{t.symbol && t.symbol.length > 0 ? t.symbol : t.address.slice(0, 6)}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="amountIn">Amount In</label>
                                <input
                                    type="number"
                                    id="amountIn"
                                    name="amountIn"
                                    value={form.amountIn}
                                    onChange={handleChange}
                                    placeholder="Amount to trade"
                                    required
                                />
                                {account && selectedTokenInfo && (
                                    <div className="amount-helper">
                                        <span>Balance: {parseFloat(selectedTokenBalance).toFixed(4)} {selectedTokenName}</span>
                                        <button type="button" className="max-link-narrow" onClick={() => setForm(f => ({ ...f, amountIn: selectedTokenBalance }))}>
                                            Max
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="form-group">
                                <label>Amount to Receive</label>
                                <input 
                                    type="text"
                                    id="amountOut"
                                    name="amountOut" 
                                    placeholder="0.0" 
                                    value={form.amountOut} 
                                    onChange={handleChange} 
                                />
                            </div>
                            <div className="create-order-actions-row">
                                {!isApproved ? (
                                    <button type="button" onClick={approveToken} disabled={!account || isApproving || !form.amountIn}>
                                        {isApproving
                                            ? 'Approving...'
                                            : (!account || !form.amountIn)
                                                ? 'Create Order'
                                                : `Approve ${getTokenName(form.tokenIn)}`}
                                    </button>
                                ) : (
                                    <button type="submit" disabled={!account}>
                                        { !account ? 'Connect Wallet' : 'Create Order' }
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                    <div className="matched-orders-wrapper">
                        <div className="matched-orders-header-row">
                            <h2>Order History</h2>
                            {matchedOrders.length > 0 && (
                                <button
                                    className="clear-matched-orders-btn"
                                    title="Clear order history"
                                    style={{ marginLeft: 12, fontSize: '0.9em', padding: '2px 8px', borderRadius: 4, cursor: 'pointer' }}
                                    onClick={() => setMatchedOrders([])}
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        <div className="matched-orders-list">
                            {matchedOrders
                                .filter(order => {
                                    // Use token symbols for matching, case-insensitive
                                    const tokenInSymbol = getTokenSymbol(form.tokenIn).toLowerCase();
                                    const tokenOutSymbol = getTokenSymbol(form.tokenOut).toLowerCase();
                                    const text = order.text.toLowerCase();
                                    return text.includes(tokenInSymbol) && text.includes(tokenOutSymbol);
                                })
                                .map((order) => (
                                    <div key={order.id} className={`matched-order-row ${order.side === 'buy' ? 'buy' : order.side === 'sell' ? 'sell' : ''}`}>
                                        <span>{order.text}</span>
                                        <span className="timestamp">{order.timestamp}</span>
                                    </div>
                                ))}
                        </div>
                        {/* Tiny chart below order history */}
                        <div className="matched-orders-pair-chart">
                            {isChartSupported(form.tokenIn, form.tokenOut) ? (
                                <PairChart pair={getPairKey()} pairLabel={pairName} />
                            ) : (
                                <div className="pair-chart-unsupported">No price history for this pair.</div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="order-books-wrapper">
                    <h2>Order Book</h2>
                    <div className="order-books-container">
                        <div className="order-book">
                            <h3>Buy Orders (Bids)</h3>
                            <div className="order-book-list">
                                {buyOrders.length === 0 ? <p>No buy orders.</p> : buyOrders.map(order =>
                                    renderOrderRow(order, 'buy', { account, tokenMap, addToast, cancelOrder, currentTime, formatDisplayValue, formatTime })
                                )}
                            </div>
                        </div>
                        <div className="order-book">
                            <h3>Sell Orders (Asks)</h3>
                            <div className="order-book-list">
                                {sellOrders.length === 0 ? <p>No sell orders.</p> : sellOrders.map(order =>
                                    renderOrderRow(order, 'sell', { account, tokenMap, addToast, cancelOrder, currentTime, formatDisplayValue, formatTime })
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <SellerMatchModal
                isOpen={sellerMatchModal.isOpen}
                message={sellerMatchModal.message}
                onClose={() => setSellerMatchModal(m => ({ ...m, isOpen: false }))}
                onConfirm={(buyOrder, buySig, sellOrder, sellSig) => {
                    handleMatchOrder(buyOrder, buySig, sellOrder, sellSig);
                    setSellerMatchModal(m => ({ ...m, isOpen: false }));
                }}
                buyOrder={sellerMatchModal.buyOrder}
                buySig={sellerMatchModal.buySig}
                sellOrder={sellerMatchModal.sellOrder}
                sellSig={sellerMatchModal.sellSig}
            />
        </div>
    </ThemeProvider>
    );
} // End of App component
